import { useEffect, useRef, useState } from 'react';

export type SkeletonPhase = 'skeleton' | 'leaving' | 'content';

export function useSkeletonGate(
  ready: boolean,
  { delay = 280, hold = 600, leave = 200 } = {},
): { phase: SkeletonPhase; reveal: boolean } {
  const [phase, setPhase] = useState<SkeletonPhase>(ready ? 'content' : 'skeleton');
  const [reveal, setReveal] = useState(!ready);
  const since = useRef<number | null>(null);

  useEffect(() => {
    if (!ready) {
      since.current ??= performance.now();
      setPhase('skeleton');
      setReveal(true);
      return;
    }
    const start = since.current;
    since.current = null;
    const elapsed = start === null ? 0 : performance.now() - start;
    if (start === null || elapsed < delay) {
      setPhase('content');
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    const leaveAt = Math.max(0, delay + hold - elapsed);
    timers.push(
      setTimeout(() => {
        setPhase('leaving');
        timers.push(setTimeout(() => setPhase('content'), leave));
      }, leaveAt),
    );
    return () => timers.forEach(clearTimeout);
  }, [ready, delay, hold, leave]);

  return { phase: ready ? phase : 'skeleton', reveal };
}
